import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnnotationObject } from "../../object-templates/annotation";
import { getDefaultObjectFromObjectAttributes } from "../../helpers/common";
import { isNil, pathOr, propOr } from "ramda";
import classes from "./object-details-modal.module.scss";
import { OBJECT_TYPES } from "../../constants/project";
import { Drawer, Form, Select } from "antd";
import { ImageObject } from "../../object-templates/image";
import { FieldObject } from "../../object-templates/field";
import {
  getDetectedFieldInitData,
  mergeDetectedFieldDefaultValue,
} from "../../helpers/detectedField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";

const withLoadData = (WrapperComponent) => {
  const WithLoadData = (props) => {
    const autoFocusFieldRef = useRef(null);
    const [form] = Form.useForm();

    const setAutoFocus = () => {
      if (props.isOpen && autoFocusFieldRef.current) {
        props.isOpen && autoFocusFieldRef.current.focus();
      }
    };

    const [currentType, setCurrentType] = useState(
      props.object.data.objectType
    );
    const [attributes, setAttributes] = useState(AnnotationObject.attributes);

    const onChangeType = (a) => {
      setCurrentType(a);
    };

    const isEditing = useMemo(() => {
      return !isNil(pathOr(null, ["data", "objectId"], props.object));
    }, [props.object]);

    const initData = useMemo(() => {
      if (isEditing) {
        if (currentType === OBJECT_TYPES.DETECTED_FIELD) {
          return getDetectedFieldInitData(props.object.data, attributes);
        }
        return props.object.data;
      }

      const data = propOr({}, "fieldProperties", props.object);
      const defaultValue = getDefaultObjectFromObjectAttributes(attributes);
      if (currentType === OBJECT_TYPES.DETECTED_FIELD) {
        return mergeDetectedFieldDefaultValue(
          defaultValue,
          props.object.data,
          attributes
        );
      }
      return {
        ...defaultValue,
        x: data.rectangle[0],
        y: data.rectangle[1],
        width: data.rectangle[2],
        height: data.rectangle[3],
        pages: data.pages ? data.pages : data.pageIndex,
      };
    }, [attributes, props.object]);

    const getAttributes = (currentType) => {
      switch (currentType) {
        case OBJECT_TYPES.ANNOTATION: {
          setAttributes(AnnotationObject.attributes);
          break;
        }
        case OBJECT_TYPES.IMAGE: {
          setAttributes(ImageObject.attributes);
          break;
        }

        case OBJECT_TYPES.DETECTED_FIELD: {
          setAttributes(FieldObject.attributes);
          break;
        }

        default:
          setAttributes(AnnotationObject.attributes);
      }
    };

    useEffect(() => {
      getAttributes(currentType);
    }, [currentType]);

    if (!initData) {
      return null;
    }

    return (
      <Drawer
        title={
          <div className={classes.TitleWrap}>
            Object Details
            <div className={classes.ButtonsWrap}>
              <button
                className={classNames(classes.Button, "btn btn-primary me-2")}
                onClick={form.submit}
              >
                Save
              </button>

              <button
                className={classNames(
                  classes.Button,
                  "btn btn-outline-secondary "
                )}
                onClick={(e) => props.handleHide(e)}
              >
                Cancel
              </button>
            </div>
          </div>
        }
        placement="right"
        open={props.isOpen}
        footer={null}
        onClose={(e) => {
          props.handleHide(e);
        }}
        width={500}
        maskClosable={false}
        className={classes.FormModal}
        afterOpenChange={setAutoFocus}
        closeIcon={
          <div className={classNames(classes.CloseIconWrap)}>
            <FontAwesomeIcon
              icon={faClose}
              className={classes.CloseIcon}
              onClick={(e) => {
                props.handleHide(e);
              }}
            />
          </div>
        }
      >
        {!isEditing &&
          [OBJECT_TYPES.IMAGE, OBJECT_TYPES.ANNOTATION].includes(
            props.object.data.objectType
          ) && (
            <div className={classes.ObjectTypeSelector}>
              <div className={"text-primary fw-bold"}>Object type:</div>
              <Select
                className={classes.Selector}
                onChange={onChangeType}
                value={currentType}
              >
                <Select.Option value={OBJECT_TYPES.ANNOTATION}>
                  Text
                </Select.Option>
                <Select.Option value={OBJECT_TYPES.IMAGE}>Image</Select.Option>
              </Select>
            </div>
          )}
        <WrapperComponent
          {...props}
          attributes={attributes}
          form={form}
          initData={initData}
          type={currentType}
          isEditing={isEditing}
          autoFocusFieldRef={autoFocusFieldRef}
        />
      </Drawer>
    );
  };

  return WithLoadData;
};

export default withLoadData;
